import React from 'react'
import { ComponentConfiguration } from '@configs/types'
import gsap from 'gsap'
import { BaseGameType } from 'game-ui-types'

export const formatDate = (date: string, separator = '/', pad = false): string => {
    const d = new Date(date)
    const formatValue = (unit: number) => `${unit}`.padStart(2, pad ? '0' : '')

    return `${formatValue(d.getDate())}${separator}${formatValue(d.getMonth() + 1)}${separator}${d.getFullYear()}`
}

// 00:00{:00}
export const formatTime = (date: string, showSeconds = false, separator = ':'): string => {
    const d = new Date(date)
    const padTime = (unit: number) => `${unit}`.padStart(2, '0')
    const seconds = showSeconds ? `${separator}${padTime(d.getSeconds())}` : ''

    return `${padTime(d.getHours())}${separator}${padTime(d.getMinutes())}${seconds}`
}

export function getLocalStorageFlagValue(key: string, defaultValue: boolean): boolean {
    try {
        return localStorage.getItem(key) === 'true'
    } catch {
        return defaultValue
    }
}

type positionStyle = {
    left?: string
    right?: string
}

export const getBuyBonusDoubleXPositions = (
    gameType: BaseGameType,
    isLeftHandMode: boolean,
    buyBonusConfig: ComponentConfiguration,
    doubleChanceConfig: ComponentConfiguration,
    reelSizes: { width: number; height: number; x: number; y: number },
    hasBuyBonus = true,
    hasDoubleChance = true
): { doubleChanceX: positionStyle; buyBonusX: positionStyle } => {
    let buyBonusPosition = 0
    let doubleChancePosition = 0
    let direction = isLeftHandMode ? 'right' : 'left'
    const remainingSpace = reelSizes.x
    const bonusWidth = hasBuyBonus ? Number(buyBonusConfig.dimensions?.width?.replace('px', '') ?? 0) : 0
    const doubleWidth = hasDoubleChance ? Number(doubleChanceConfig.dimensions?.width?.replace('px', '') ?? 0) : 0
    const isConfigD = gameType === BaseGameType.SLOT_CONFIG_D
    const isConfigE = gameType === BaseGameType.SLOT_CONFIG_E

    if (gameType === BaseGameType.SLOT_CONFIG_A) {
        // Buttons are underneath eachother in config A so the x is the same for both
        buyBonusPosition = doubleChancePosition = Math.max(remainingSpace / 2 - bonusWidth / 2, 0)
    } else {
        // In other configs the double is on the left and bonus on the right in that order always within the section
        // As a section they can appear on the left or right of the screen/stage depending on the config and if isLeftHandMode enabled
        if (isConfigD || isConfigE) {
            direction = isLeftHandMode ? 'left' : 'right'
        }

        const bonusDefaultX = Number(buyBonusConfig.position?.left?.replace('px', '') ?? 0)
        const doubleDefaultX = Number(doubleChanceConfig.position?.left?.replace('px', '') ?? 0)
        const padding = !hasBuyBonus || !hasDoubleChance ? 0 : bonusDefaultX - doubleDefaultX - doubleWidth
        const sectionWidth = doubleWidth + padding + bonusWidth
        let leftButtonX = remainingSpace / 2 - sectionWidth / 2
        let rightButtonX = leftButtonX + padding + doubleWidth

        if (isLeftHandMode) {
            const tempLeft = leftButtonX

            leftButtonX = rightButtonX
            rightButtonX = tempLeft
        }

        if (direction === 'left' && leftButtonX < 0) {
            leftButtonX = 0
            rightButtonX = Math.abs(doubleWidth + padding)
        } else if (direction === 'right' && rightButtonX < 0) {
            rightButtonX = 0
            leftButtonX = Math.abs(doubleWidth + padding)
        }

        doubleChancePosition = leftButtonX
        buyBonusPosition = rightButtonX
    }

    return {
        doubleChanceX: { [direction]: `${doubleChancePosition}px` },
        buyBonusX: { [direction]: `${buyBonusPosition}px` },
    }
}

export const fadeGSAPAnimation = (show: boolean, targets: HTMLDivElement[], duration = 0.1, ease = 'linear') => {
    return gsap.to(targets, {
        opacity: Number(show),
        ease,
        duration,
        onStart: () => {
            if (show) {
                targets.forEach((t) => {
                    t.style.visibility = 'visible'
                    t.inert = false
                })
            }
        },
        onComplete: () => {
            if (!show) {
                targets.forEach((t) => {
                    t.style.visibility = 'hidden'
                    t.inert = true
                })
            }
        },
    })
}

export const withAutoBlur = (fn: (e?: React.MouseEvent<HTMLButtonElement>) => void) => {
    return (e?: React.MouseEvent<HTMLButtonElement>) => {
        if (e?.button === 2) return

        // needed because of an issue with some browsers extensions monkeypatching dom elements
        // and causing click events to be triggered - STEAL-5413
        if (!e?.isTrusted) return

        e?.currentTarget.blur()
        fn(e)
    }
}

export const extractScale = (style: string): number | null => {
    const match = style.match(/scale-\[(.*?)\]/)

    return match ? Number(match[1]) : null
}

export const patchPaytableUrl = (language: string) => {
    const params = new URLSearchParams(window.location.search)

    const raw = params.get('paytableUrl')

    if (!raw) return

    const decoded = decodeURIComponent(raw)

    const lang = language.slice(0, 2).toLowerCase()

    const patched = decoded.replace(/\/[a-z]{2}\.html(?=\?)/i, `/${lang}.html`)

    params.set('paytableUrl', patched)

    window.history.replaceState({}, '', `?${params.toString()}`)
}

export const wait = (duration: number) => new Promise((resolve) => setTimeout(resolve, duration))

export function flattenObject<R = unknown>(object: Record<string, unknown>, keyPrefix?: string): Record<string, R> {
    const flatObject: Record<string, R> = {}

    Object.keys(object).forEach((key) => {
        const value = object[key]
        const propertyKey = keyPrefix ? `${keyPrefix}.${key}` : key

        if (typeof value === 'object' && !Array.isArray(value)) {
            Object.assign(flatObject, flattenObject(value as Record<string, unknown>, propertyKey))
        } else {
            flatObject[propertyKey] = value as R
        }
    })

    return flatObject
}
